/* eslint-disable max-statements */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import {
  EditOrderProductRequestDto,
  EditOrderRequestDto,
  EditOrderDiscountRequestDto,
} from '@tiendanube/common';
import { useAppDispatch } from 'App/store';
import { useOrderDetails } from 'domains/Orders/Orders/hooks';
import {
  cleanEditOrder,
  editOrderById,
  getEditOrderStatus,
} from 'domains/Orders/Orders/ordersSlice';
import { useOrderDiscounts } from '../../hooks';
import { OrderEditProduct, OrderEditDiscount } from '../components/Products';
import {
  OrderDiscountType,
  OrderEditDiscountType,
} from '../components/Products/types';

export function useEditOrder(id: string) {
  const { orderDetails, isError, isLoading } = useOrderDetails(id);
  const [isOrderEdited, setIsOrderEdited] = useState(false);
  const [editedOrderProducts, setEditedOrderProducts] = useState<
    OrderEditProduct[]
  >(orderDetails?.products ?? []);
  const [editedOrderDiscounts, setEditedOrderDiscounts] = useState<
    OrderEditDiscount[]
  >([]);
  const [editReason, setEditReason] = useState<string | undefined>(undefined);
  const [notifyCustomer, setNotifyCustomer] = useState<boolean>(false);
  const { isLoading: isLoadingEdit, status: editStatus } =
    useSelector(getEditOrderStatus);
  const dispatch = useAppDispatch();

  const editOrder = useCallback(
    async (
      updatedOrderProducts: OrderEditProduct[],
      updatedOrderDiscounts: OrderEditDiscount[],
      sendNotification: boolean,
      skipShippingRequote: boolean,
      reason?: string,
    ) => {
      if (!orderDetails) return;

      const toEditOrderRequestProduct = (p: OrderEditProduct) => {
        if (p.editType === 'add') {
          return {
            productId: p.productId,
            variantId: p.variantId,
            quantity: p.quantity,
            type: 'add',
            fulfillmentOrderId: p.fulfillmentOrderId,
          } as EditOrderProductRequestDto;
        }

        if (!p.originalQuantity) {
          // Should never happen
          throw new Error(
            `Product with variantId ${p.variantId} not found for order ${id} when editing the order`,
          );
        }
        const quantityDiff = p.quantity - p.originalQuantity;

        return {
          lineItemId: p.id,
          quantity: p.editType !== 'edit' ? p.quantity : Math.abs(quantityDiff),
          type: quantityDiff > 0 ? 'add' : 'remove',
          restoreStock: p.restoreStock,
          fulfillmentOrderId: p.fulfillmentOrderId,
        } as EditOrderProductRequestDto;
      };

      const toEditOrderRequestDiscount = (d: OrderEditDiscount) => ({
        type: d.discountType,
        value: d.value,
        action: d.editType,
      });

      const parseUpdatedOrderDiscounts = (
        discounts: OrderEditDiscount[],
      ): OrderEditDiscount[] => {
        const hasEditCoupon = discounts.some(
          (d) => d.id === 'editCouponDiscount' && d.editType === 'add',
        );
        const hasCoupon = discounts.some(
          (d) => d.id === 'couponDiscount' && d.editType === 'remove',
        );

        // orderDetails.amounts.freeShippingCoupon is required as a workaround for free shipping coupons with value: null.
        // These coupons are not shown in the order details, and thus cannot be manually removed.
        // So we change the EditDiscount request type from 'add' to 'replace' in those cases.
        const freeShippingCouponIsNull =
          orderDetails.amounts.freeShippingCoupon &&
          orderDetails.amounts.discount === null;

        if (hasEditCoupon && (hasCoupon || freeShippingCouponIsNull)) {
          return discounts
            .map((d) => {
              if (d.id === 'editCouponDiscount' && d.editType === 'add') {
                return { ...d, editType: 'replace' as OrderEditDiscountType };
              }
              return d;
            })
            .filter((d) => d.id !== 'couponDiscount');
        }

        return discounts;
      };

      const editRequest: EditOrderRequestDto = {
        products: updatedOrderProducts
          .filter((p) => p.editType !== undefined)
          .map(toEditOrderRequestProduct),
        discounts: parseUpdatedOrderDiscounts(updatedOrderDiscounts)
          .filter((d) => d.editType !== undefined)
          .map(toEditOrderRequestDiscount)
          .filter((d) => d !== undefined) as EditOrderDiscountRequestDto[],
        reason,
        notifyCustomer: sendNotification,
        skipShippingRequote,
      };
      unwrapResult(await dispatch(editOrderById({ id, editRequest })));
      dispatch(cleanEditOrder());
    },
    [id, orderDetails, dispatch],
  );

  const cleanEditState = useCallback(() => {
    dispatch(cleanEditOrder());
  }, [dispatch]);

  const nonDeletedEditedProducts = useMemo(
    () => editedOrderProducts.filter((p) => p.editType !== 'delete'),
    [editedOrderProducts],
  );

  const noDeletedFulfillments = useMemo(() => {
    if (
      !orderDetails?.fulfillmentOrders ||
      orderDetails.fulfillmentOrders.length === 0
    ) {
      const editedProductsCount = nonDeletedEditedProducts.reduce(
        (acc, p) => acc + p.quantity,
        0,
      );
      return editedProductsCount > 0;
    }

    const editedFFOO = nonDeletedEditedProducts
      .map((product) => product.fulfillmentOrderId)
      .filter((ffoId, idx, ffoIds) => ffoIds.indexOf(ffoId) === idx) // Unique FFO ids
      .filter(Boolean); // Filters out 'undefined' values, but should never happen

    return editedFFOO.length === orderDetails.fulfillmentOrders.length;
  }, [orderDetails?.fulfillmentOrders, nonDeletedEditedProducts]);

  const isValidEdition = useMemo(
    () =>
      !nonDeletedEditedProducts.some(
        (p) => p.maxStock && p.quantity > p.maxStock,
      ) && noDeletedFulfillments,
    [nonDeletedEditedProducts, noDeletedFulfillments],
  );

  const editedProductsWithFulfillments: OrderEditProduct[] = useMemo(() => {
    if (!orderDetails?.fulfillmentOrders) {
      return (
        orderDetails?.products.map((p) => ({
          ...p,
          originalQuantity: p.quantity,
        })) ?? []
      );
    } else {
      const ffoProducts = orderDetails.fulfillmentOrders.flatMap((ffo) =>
        ffo.products.map(
          (product) =>
            ({
              ...product,
              originalQuantity: product.quantity,
              fulfillmentOrderId: ffo.id,
              locationId: ffo.assignedLocation.id,
            } as OrderEditProduct),
        ),
      );
      return ffoProducts;
    }
  }, [orderDetails]);

  const editedDiscountsFromOrder: OrderEditDiscount[] = useOrderDiscounts(
    orderDetails?.amounts,
    orderDetails?.payment.method,
  )
    .filter(({ discountType }) => !!discountType)
    .map(({ id, name, value, discountType, prefix, appearance }) => ({
      id,
      name,
      value,
      discountType: discountType as OrderDiscountType,
      removed: false,
      prefix,
      appearance,
    }));

  useEffect(() => {
    setEditedOrderProducts(editedProductsWithFulfillments);
    setEditedOrderDiscounts(editedDiscountsFromOrder);

    return () => {
      cleanEditState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderDetails,
    setEditedOrderProducts,
    editedProductsWithFulfillments,
    setEditedOrderDiscounts,
    cleanEditState,
  ]);

  return {
    isLoading: isLoading || (!editedOrderProducts.length && !isError),
    isError,
    isLoadingEdit,
    editStatus,
    editedOrderProducts,
    setEditedOrderProducts,
    editedOrderDiscounts,
    setEditedOrderDiscounts,
    orderDetails,
    isOrderEdited,
    setIsOrderEdited,
    editReason,
    setEditReason,
    editOrder,
    cleanEditState,
    nonDeletedEditedProducts,
    isValidEdition,
    notifyCustomer,
    setNotifyCustomer,
  };
}
